import * as Dialog from '@radix-ui/react-dialog';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { Input } from '@/components/ui/input';
import { AddressField } from '@/components/address/address-field';
import { addressOf, addressValueOf } from '../address';
import { addressLine } from '../page/maps';
import { addSite, siteBySign, type Site, type SiteCategory, type Supplier } from './api';
import { Field, SITE_CATEGORIES } from './CreateSupplierDialog';

const ADD = '__add__';

/** Whether the form asks for the site: more than one active site, or one already chosen. */
export function asksSite(supplier: Supplier, siteId: string): boolean {
  return supplier.sites.filter((site) => site.active).length >= 2 || siteId !== '';
}

function siteLabel(site: Site, officeWord: string): string {
  const parts = [site.sign, site.registeredOffice ? officeWord : null, addressLine(site.address)];
  return parts.filter((part) => part !== null && part !== '').join(' · ');
}

/**
 * «Sede · facoltativa» (board 1.3; report §3.2.5): which of the supplier's sites, or a new one. Left
 * empty, the expense's place applies (contract `updateExpenseSupplier`).
 */
export function SiteSelect({
  id,
  supplier,
  value,
  place,
  onChange,
  onAdopt,
}: {
  readonly id: string;
  readonly supplier: Supplier;
  readonly value: string;
  readonly place: string;
  readonly onChange: (siteId: string) => void;
  readonly onAdopt: (supplier: Supplier) => void;
}) {
  const { t } = useTranslation('suppliers');
  const [adding, setAdding] = useState(false);
  const sites = supplier.sites.filter((site) => site.active || site.id === value);
  const choices = [
    { value: '', label: t('picker.site.none') },
    ...sites.map((site) => ({ value: site.id, label: siteLabel(site, t('picker.site.office')) })),
    ...(supplier.can.addSite ? [{ value: ADD, label: `+ ${t('picker.site.add')}` }] : []),
  ];
  const count = supplier.sites.filter((site) => site.active).length;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {t('picker.site.label')}{' '}
        <span className="text-muted-foreground font-normal">{t('picker.site.optional')}</span>
      </label>
      <ChoiceSelect
        triggerClassName="data-[size=default]:min-h-11"
        id={id}
        value={value}
        choices={choices}
        onChange={(next) => (next === ADD ? setAdding(true) : onChange(next))}
      />
      <small className="text-muted-foreground text-xs">
        {place.trim() === ''
          ? t('picker.site.hintNoPlace', { count })
          : t('picker.site.hint', { count, place: place.trim() })}
      </small>
      {adding && (
        <AddSiteDialog
          supplier={supplier}
          onClose={() => setAdding(false)}
          onAdded={(next, siteId) => {
            setAdding(false);
            onAdopt(next);
            if (siteId) onChange(siteId);
          }}
        />
      )}
    </div>
  );
}

/** «Aggiungi una sede»: sign, type and address; added at once and chosen for the expense. */
function AddSiteDialog({
  supplier,
  onClose,
  onAdded,
}: {
  readonly supplier: Supplier;
  readonly onClose: () => void;
  readonly onAdded: (supplier: Supplier, siteId: string | null) => void;
}) {
  const { t } = useTranslation('suppliers');
  const uid = useId();
  const [sign, setSign] = useState('');
  const [category, setCategory] = useState<SiteCategory | ''>('');
  const [address, setAddress] = useState(() => addressValueOf(null, supplier.country));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const categories = [
    { value: '', label: t('picker.addSite.categoryNone') },
    ...SITE_CATEGORIES.map((value) => ({ value, label: t(`category.${value}`) })),
  ];
  const submit = async (): Promise<void> => {
    setBusy(true);
    setFailed(false);
    const written = addressOf(address);
    const outcome = await addSite(supplier.id, {
      sign: sign.trim() || null,
      category: category === '' ? null : category,
      ...(written ? { address: written } : {}),
    });
    setBusy(false);
    if (!outcome.ok) {
      setFailed(true);
      return;
    }
    onAdded(outcome.value, siteBySign(outcome.value, sign.trim()));
  };
  return (
    <Dialog.Root open onOpenChange={(open) => (open ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)]" />
        <Dialog.Content className="border-border bg-card text-foreground fixed top-1/2 left-1/2 z-50 flex w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-xl border p-5 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">
            {t('picker.addSite.title', { name: supplier.name })}
          </Dialog.Title>
          <Dialog.Description className="text-muted-foreground text-sm">
            {t('picker.addSite.text')}
          </Dialog.Description>
          <form
            noValidate
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Field id={`${uid}-sign`} label={t('picker.addSite.sign')}>
              <Input
                id={`${uid}-sign`}
                className="min-h-11"
                value={sign}
                onChange={(event) => setSign(event.target.value)}
              />
            </Field>
            <Field id={`${uid}-category`} label={t('picker.addSite.category')}>
              <ChoiceSelect
                triggerClassName="data-[size=default]:min-h-11"
                id={`${uid}-category`}
                value={category}
                choices={categories}
                onChange={(next) => setCategory(next as SiteCategory | '')}
              />
            </Field>
            <div className="sm:col-span-2">
              <AddressField
                label={t('picker.addSite.address')}
                value={address}
                onChange={setAddress}
              />
            </div>
            {failed && (
              <p role="alert" className="text-destructive text-sm sm:col-span-2">
                {t('picker.addSite.failed')}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2 sm:col-span-2">
              <Button type="button" variant="outline" className="min-h-11" onClick={onClose}>
                {t('picker.addSite.cancel')}
              </Button>
              <Button type="submit" className="min-h-11" disabled={busy}>
                {t('picker.addSite.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
